import {t} from "@lingui/core/macro";
import {css, StyleSheet} from "aphrodite";
import React from "react";

import Button from "@khanacademy/wonder-blocks-button";
import {View} from "@khanacademy/wonder-blocks-core";
import {OptionItem, SingleSelect} from "@khanacademy/wonder-blocks-dropdown";
import {Checkbox, TextField} from "@khanacademy/wonder-blocks-form";
import {PhosphorIcon} from "@khanacademy/wonder-blocks-icon";
import {LabeledField} from "@khanacademy/wonder-blocks-labeled-field";
import Link from "@khanacademy/wonder-blocks-link";
import {font, semanticColor, sizing} from "@khanacademy/wonder-blocks-tokens";
import Tooltip from "@khanacademy/wonder-blocks-tooltip";
import {
    Body,
    HeadingMedium,
    LabelMedium,
} from "@khanacademy/wonder-blocks-typography";
import {
    getLoginErrorMessageByCode,
    loginWithPassword,
    type Options as LoginOptions,
} from "@khan/auth-password";
import {
    calculateNextUrl,
    TextFieldWithInfo,
    type Role,
} from "@khan/auth-shared";
import {getPass} from "@khan/data-cookies";
import {useGql, type GqlFetchFn} from "@khan/data-gql";
import {doNotTranslate, doNotTranslateYet} from "@khan/i18n";
import {RequestInfo} from "@khan/shared-core";
import {
    getContinueUrl,
    getForgotPasswordUrl,
    preserveMarketingParams,
    redirect,
    useLocation,
    type Location,
} from "@khan/urls";

import devUsers from "./dev-users.ts"; // Only used on dev-appserver
import {LoginLegalBlurb} from "./legal-blurb.tsx";
import {OAuthButtons} from "./oauth-buttons.tsx";

import infoIcon from "@phosphor-icons/core/bold/info-bold.svg";
import warningCircleIcon from "@phosphor-icons/core/bold/warning-circle-bold.svg";

interface Props {
    /**
     * The html tag to use for the login title. Defaults to h1.
     */
    loginTitleTag?: string;
    /**
     * The text of the heading
     */
    loginTitleText?: string;
    /**
     * The class code to use for sign up.
     */
    classCode?: string;
    /**
     * The url to redirect to after login. Note: this can be overridden by the
     * `continue` query parameter in the url.
     */
    continueUrl?: string;
    /**
     * The user's primary role when using the site.
     */
    role?: Role;
    /**
     * Whether to show the legal blurb.
     */
    showLegalBlurb?: boolean;
    /**
     * The legal blurb that displays beneath the login button
     */
    legalBlurb?: React.ReactNode;
    /**
     * Optional custom login handler that uses the same Options type as loginWithPassword
     */
    loginCall?: (options: LoginOptions) => Promise<void>;
    /**
     * If true, renders the "Need a Khan Academy account? Sign up today" text
     */
    renderSignup?: boolean;
    /**
     * Show the OAuth buttons or not.
     */
    showOAuth?: boolean;
    /**
     * Whether to only show the icon and not the text. The list will display
     * horizontally.
     */
    iconOnly?: boolean;
    /**
     * Callback triggered after a user has logged in, but before they are
     * redirected to the next page.
     */
    onSubmitSuccess?: () => Promise<void>;
    /**
     * A component to ask the user if they need an account.
     */
    signupComponent?: React.ReactNode;
    /**
     * Text which displays below the heading. Provides extra info to the user
     */
    subHeading?: string;
    /**
     * The alignment of the heading.
     */
    headingAlignment?: "left" | "center";
}

// Used to allow-list the role query param
const roleMap: Record<string, Role> = {
    learner: "learner",
    teacher: "teacher",
    parent: "parent",
};

// Credentials refers to the regular login page with username, password and OAuth
// Authenticator and recovery refer to the two factor authentication step, which
// if enabled on the account is either inputting a code from an authenticator
// app or using a recovery code.
type LoginState = "credentials" | "authenticator" | "recovery";

/**
 * A form to allow the user to log in via any method of their choice.
 * This includes email/password, Google, Facebook, Apple, and Clever
 */
export function LogInForm({
    classCode,
    continueUrl = "/",
    role,
    showLegalBlurb = true,
    legalBlurb = <LoginLegalBlurb />,
    loginCall,
    loginTitleTag = "h1",
    loginTitleText = t`Log in now!`,
    renderSignup = true,
    showOAuth = true,
    iconOnly,
    onSubmitSuccess,
    signupComponent,
    subHeading,
    headingAlignment = "left",
}: Props): React.ReactElement | null {
    // Obj hooks
    const gqlFetch = useGql();
    const location = useLocation();

    // State hooks
    const [oauthError, setOauthError] = React.useState<
        string | null | undefined
    >(null);
    const [serverError, setServerError] = React.useState<
        string | null | undefined
    >(null);
    const [identifier, setIdentifier] = React.useState("");
    const [identifierError, setIdentifierError] = React.useState<
        string | null | undefined
    >("");
    const [password, setPassword] = React.useState("");
    const [passwordError, setPasswordError] = React.useState<
        string | null | undefined
    >("");
    const [isSubmitting, setIsSubmitting] = React.useState(false);
    const [loginState, setLoginState] =
        React.useState<LoginState>("credentials");
    const [twoFactorAuthCode, setTwoFactorAuthCode] = React.useState("");
    const [twoFactorRecoveryCode, setTwoFactorRecoveryCode] =
        React.useState("");
    const [rememberTwoFactor, setRememberTwoFactor] = React.useState(false);

    // Local vars
    const cont = getContinueUrl(location) ?? continueUrl;
    const userRole = roleMap[location.queryParams["role"]] || role;

    // Functions
    function canFormBeSubmitted(): boolean {
        if (loginState === "authenticator") {
            return Boolean(
                identifier &&
                    password &&
                    // length of auth code should be 6, allowing for one space
                    twoFactorAuthCode.replace(" ", "").length === 6,
            );
        }
        if (loginState === "recovery") {
            return Boolean(
                identifier &&
                    password &&
                    // recovery code is of format aaaaa-11111
                    twoFactorRecoveryCode.trim().length === 11,
            );
        }
        return Boolean(identifier && password);
    }

    // Refs
    const usernameInputField = React.useRef<HTMLInputElement | null>(null);
    const mountedRef = React.useRef(true);

    // Add cleanup on unmount
    React.useEffect(() => {
        return () => {
            mountedRef.current = false;
        };
    }, []);

    async function doLogin(options: LoginOptions): Promise<void> {
        if (isSubmitting) {
            return;
        }
        setIsSubmitting(true);

        try {
            if (loginCall) {
                await loginCall(options);
            } else {
                await defaultLoginCall(
                    gqlFetch,
                    location,
                    options,
                    classCode,
                    cont,
                );
            }
            if (mountedRef.current && onSubmitSuccess) {
                await onSubmitSuccess();
            }
        } catch (e: any) {
            if (!mountedRef.current) {
                return;
            }

            // This is a signal that two factor authentication is enabled,
            // and we need to show the 2FA form so users can input their
            // verification codes.
            if (e.message === "TWO_FACTOR_CODE_REQUIRED") {
                setLoginState("authenticator");
                // Clear any previous errors, like incorrect password
                setServerError("");
                return;
            }

            // This is a signal that the two factor code is incorrect,
            // and we should kick back to the login form, emptied out
            // for security purposes.
            if (
                e.message === "TWO_FACTOR_CODE_INCORRECT" ||
                e.message === "TWO_FACTOR_RECOVERY_CODE_INVALID"
            ) {
                setLoginState("credentials");
                setIdentifier("");
                setPassword("");
                setTwoFactorAuthCode("");
                setTwoFactorRecoveryCode("");
            }

            // We refocus the user on the username field so it's faster to
            // fix it if that was the issue.
            if (usernameInputField.current) {
                usernameInputField.current.focus();
            }

            const errorMessage = getLoginErrorMessageByCode(e.message);
            setServerError(errorMessage);
        } finally {
            if (mountedRef.current) {
                setIsSubmitting(false);
            }
        }
    }

    // Function to handle dev user login selection
    const handleDevUserLogin = (identifier: string, password: string) => {
        setIdentifier(identifier);
        setPassword(password);

        // Trigger the login process
        doLogin({
            identifier,
            password,
        });
    };

    // Authenticator app code step of two factor authentication
    if (loginState === "authenticator") {
        return (
            <View style={styles.logInForm}>
                <HeadingMedium>{doNotTranslateYet`Two-factor authentication`}</HeadingMedium>
                <LabelMedium>
                    {doNotTranslateYet`Use your authenticator app to enter the verification code.`}
                </LabelMedium>

                <form
                    className={css(styles.form)}
                    onSubmit={(e: React.SyntheticEvent): void => {
                        e.preventDefault();
                        if (isSubmitting) {
                            return;
                        }
                        if (canFormBeSubmitted()) {
                            doLogin({
                                identifier,
                                password,
                                // clean up the string in case people put a
                                // space in the middle
                                twoFactorAuthCode: twoFactorAuthCode.replace(
                                    " ",
                                    "",
                                ),
                                rememberTwoFactor,
                            });
                        }
                    }}
                >
                    <TextFieldWithInfo
                        testId="two-factor-authenticator-code"
                        id="two-factor-authenticator-code"
                        label={doNotTranslateYet`Verification code`}
                        required={true}
                        value={twoFactorAuthCode}
                        onChange={(code) => {
                            if (code.length <= 7) {
                                setTwoFactorAuthCode(code);
                            }
                        }}
                        placeholder={doNotTranslateYet`123 456`}
                    />

                    <View style={styles.recoveryOption}>
                        <LabelMedium>
                            {doNotTranslateYet`Can't access your app? `}
                        </LabelMedium>
                        <Button
                            kind="tertiary"
                            onClick={() => setLoginState("recovery")}
                        >
                            {doNotTranslateYet`Use a recovery code`}
                        </Button>
                    </View>

                    {serverError && (
                        <ErrorMessage id="log-in-server-error">
                            {serverError}
                        </ErrorMessage>
                    )}
                    <Checkbox
                        label={doNotTranslateYet`I trust this computer`}
                        checked={rememberTwoFactor}
                        onChange={setRememberTwoFactor}
                    />
                    <Button
                        type="submit"
                        disabled={!canFormBeSubmitted()}
                        testId="two-factor-submit-button"
                        spinner={isSubmitting}
                    >
                        {doNotTranslateYet`Verify`}
                    </Button>
                </form>
            </View>
        );
    }

    // Recovery code step of two factor authentication
    if (loginState === "recovery") {
        return (
            <View style={styles.logInForm}>
                <HeadingMedium>{doNotTranslateYet`Two-factor authentication`}</HeadingMedium>
                <LabelMedium>
                    {doNotTranslateYet`Enter a backup code from your list of recovery codes.`}
                </LabelMedium>

                <form
                    className={css(styles.form)}
                    onSubmit={(e: React.SyntheticEvent): void => {
                        e.preventDefault();
                        if (isSubmitting) {
                            return;
                        }
                        if (canFormBeSubmitted()) {
                            doLogin({
                                identifier,
                                password,
                                twoFactorRecoveryCode,
                            });
                        }
                    }}
                >
                    <TextFieldWithInfo
                        testId="two-factor-recovery-code"
                        id="two-factor-recovery-code"
                        label={doNotTranslateYet`Recovery code`}
                        required={true}
                        value={twoFactorRecoveryCode}
                        onChange={(code) => {
                            if (code.length <= 11) {
                                setTwoFactorRecoveryCode(code);
                            }
                        }}
                        placeholder={doNotTranslateYet`abcde-12345`}
                    />

                    <Button
                        kind="tertiary"
                        onClick={() => setLoginState("authenticator")}
                        style={styles.leftAlignedButton}
                    >
                        {doNotTranslateYet`Use authenticator app instead`}
                    </Button>

                    {serverError && (
                        <ErrorMessage id="log-in-server-error">
                            {serverError}
                        </ErrorMessage>
                    )}
                    <Button
                        type="submit"
                        disabled={!canFormBeSubmitted()}
                        testId="two-factor-submit-button"
                        spinner={isSubmitting}
                    >
                        {isSubmitting
                            ? doNotTranslateYet`Verifying...`
                            : doNotTranslateYet`Verify`}
                    </Button>
                </form>
            </View>
        );
    }

    showOAuth = RequestInfo.IS_DEV_SERVER
        ? showOAuth && globalThis.__FASTLY_MODE__ === "frontend"
        : showOAuth;

    return (
        <View style={styles.logInForm}>
            <View style={{alignItems: headingAlignment}}>
                <HeadingMedium tag={loginTitleTag}>
                    {loginTitleText}
                </HeadingMedium>
                {subHeading && (
                    <LabelMedium style={styles.subHeading}>
                        {subHeading}
                    </LabelMedium>
                )}
            </View>

            {showOAuth && (
                <>
                    <OAuthButtons
                        continueUrl={cont}
                        classCode={classCode}
                        role={userRole}
                        setServerError={setOauthError}
                        iconOnly={iconOnly}
                    />
                    {oauthError && <ErrorMessage>{oauthError}</ErrorMessage>}
                </>
            )}

            <DevUserLogins doLogin={handleDevUserLogin} />

            <View style={styles.fieldsHeader}>
                <Body tag="span" style={styles.fieldsHeaderText}>
                    {t`Or log in with email`}
                </Body>
            </View>

            <form
                className={css(styles.form)}
                onSubmit={(e: React.SyntheticEvent): void => {
                    e.preventDefault();

                    // NOTE(diedra): The button is not disabled while submitting
                    // the form so the user doesn't lose focus if they tabbed to
                    // the button to submit the form.
                    if (isSubmitting) {
                        return;
                    }

                    if (canFormBeSubmitted()) {
                        doLogin({
                            identifier,
                            password,
                        });
                    }
                }}
            >
                {/* Username Field */}
                <LabeledField
                    testId="identifier"
                    id="username"
                    label={t`Email or username`}
                    contextLabel={t`required`}
                    errorMessage={identifierError}
                    field={
                        <TextField
                            aria-required="true"
                            name="username"
                            autoComplete="username"
                            required={true}
                            value={identifier}
                            onChange={(newVal) => setIdentifier(newVal)}
                            placeholder={t`email@example.com`}
                            validate={(val) =>
                                !val
                                    ? t`An email or username is required to log in. ` +
                                      `You can also log in with Google, Facebook, Apple, or Clever.`
                                    : null
                            }
                            onValidate={setIdentifierError}
                        />
                    }
                />

                {/* Password Field */}
                <LabeledField
                    id="current-password"
                    testId="password"
                    label={t`Password`}
                    description={
                        <Body
                            style={styles.passwordDescription}
                        >{t`Password must be at least 8 characters and have a special character`}</Body>
                    }
                    contextLabel={t`required`}
                    errorMessage={passwordError}
                    field={
                        <TextField
                            aria-required="true"
                            name="current-password"
                            autoComplete="current-password"
                            type="password"
                            value={password}
                            onChange={(newVal) => setPassword(newVal)}
                            validate={(val) =>
                                !val
                                    ? t`A password is required to log in.`
                                    : null
                            }
                            onValidate={setPasswordError}
                        />
                    }
                />

                <Link
                    style={styles.forgotPassword}
                    href={
                        RequestInfo.isExtension
                            ? getForgotPasswordUrl({extension: "1"})
                            : getForgotPasswordUrl()
                    }
                >
                    {t`Forgot password?`}
                </Link>

                {serverError && (
                    <ErrorMessage id="log-in-server-error">
                        {serverError}
                    </ErrorMessage>
                )}

                <Button
                    type="submit"
                    disabled={!canFormBeSubmitted()}
                    testId="log-in-submit-button"
                >
                    {isSubmitting ? t`Logging you in...` : t`Log in`}
                </Button>

                {showLegalBlurb && legalBlurb}

                {renderSignup && signupComponent}
            </form>
        </View>
    );
}

function defaultLoginCall(
    gqlFetch: GqlFetchFn,
    location: Location,
    options: LoginOptions,
    classCode?: string,
    continueUrl: string = "/",
) {
    return loginWithPassword(gqlFetch, {
        ...options,
        signupCodes: classCode ? [classCode] : undefined,
    }).then((userData) => {
        calculateNextUrl({
            gqlFetch,
            location,
            loginType: "password",
            continueUrl: continueUrl,
            isFirstLogin: userData.isFirstLogin,
            joinClassrooms: userData.joinClassrooms,
        }).then((calculatedNextUrl) => {
            redirect(
                preserveMarketingParams(
                    calculatedNextUrl,
                    new URLSearchParams(location.search),
                ),
            );
        });
    });
}

function ErrorMessage({
    children,
    id,
}: {
    children: string;
    id?: string;
}): React.ReactNode {
    return (
        <View
            id={id}
            aria-live="polite"
            role="alert"
            style={styles.errorMessageRow}
        >
            <PhosphorIcon
                icon={warningCircleIcon}
                color={semanticColor.status.critical.foreground}
            />
            <Body tag="p" style={styles.errorMessage}>
                {children}
            </Body>
        </View>
    );
}

function DevUserLogins({
    doLogin,
}: {
    doLogin: (identifier: string, password: string) => void;
}): React.ReactNode {
    if (!RequestInfo.IS_DEV_SERVER || getPass("use_prod_backend")) {
        return null;
    }

    // These are pre-filled users that are created when we create
    // current.sqlite: devUsers is a list of users that the
    // developer can log in as; each user is an object containing
    // their email address and bio.
    const userOptions = devUsers.map((user) => (
        <OptionItem
            key={user.username}
            label={`${user.username}: ${user.bio}`}
            value={`${user.username}:${user.password}`}
        />
    ));

    // Third-party login methods work in "frontend" mode so only show the tooltip
    // if we're in a different mode.
    const showTooltip = globalThis.__FASTLY_MODE__ !== "frontend";

    return (
        <View style={styles.devUserLogin}>
            <SingleSelect
                aria-label="User"
                onChange={(selectedValue: string): void => {
                    const parts = selectedValue.split(":");
                    const identifier = parts[0];
                    const password = parts.slice(1).join(":");

                    doLogin(identifier, password);
                }}
                placeholder="OR log in as an existing user:"
                style={{marginInlineEnd: `${sizing.size_040}px`}}
            >
                {userOptions}
            </SingleSelect>
            {showTooltip && (
                <Tooltip
                    content={
                        doNotTranslate`NOTE: third-party login methods do not work in ` +
                        `dev, by default. To enable third-party logins, ` +
                        `uncomment the code in services/users/cmd/serve/main.go ` +
                        `and ./apps/khanacademy/src/auth/new_components/log-in-form.tsx!`
                    }
                >
                    <PhosphorIcon
                        icon={infoIcon}
                        color={
                            semanticColor.core.foreground.instructive.default
                        }
                    />
                </Tooltip>
            )}
        </View>
    );
}

const styles = StyleSheet.create({
    logInForm: {
        gap: sizing.size_160,
    },
    form: {
        display: "flex",
        flexDirection: "column",
        gap: sizing.size_160,
    },
    fieldsHeader: {
        display: "flex",
        alignItems: "center",
        justifyContent: "center",
        position: "relative",
        marginBlockEnd: sizing.size_160,
        ":after": {
            content: "''",
            position: "absolute",
            top: "50%",
            height: 1,
            width: "100%",
            backgroundColor: semanticColor.core.border.neutral.subtle,
        },
    },
    fieldsHeaderText: {
        fontSize: font.size.small,
        backgroundColor: semanticColor.core.background.base.default,
        padding: `0 ${sizing.size_100}`,
        position: "relative",
        zIndex: 1,
    },
    passwordDescription: {
        fontSize: font.size.xSmall,
        lineHeight: font.lineHeight.xSmall,
    },
    errorMessage: {
        color: semanticColor.status.critical.foreground,
    },
    errorMessageRow: {
        display: "flex",
        flexDirection: "row",
        alignItems: "center",
        gap: sizing.size_080,
    },
    forgotPassword: {
        fontSize: font.size.medium,
        lineHeight: font.lineHeight.medium,
        fontWeight: font.weight.semi,
        textDecorationLine: "underline",
    },
    devUserLogin: {
        display: "flex",
        flexDirection: "row",
        alignItems: "center",
    },
    subHeading: {
        color: semanticColor.core.foreground.neutral.subtle,
        paddingBlockStart: sizing.size_080,
    },
    recoveryOption: {
        display: "flex",
        flexDirection: "row",
        alignItems: "baseline",
        gap: sizing.size_080,
    },
    leftAlignedButton: {
        alignSelf: "flex-start",
    },
});
